/**
 * On-phone diagnostics for taps that land in the wrong place: open the site
 * with ?debug=1 (sticks until ?debug=0) — on local dev and the test site only. A strip at the top shows the page's
 * scroll, the visual viewport (keyboard, pinch zoom) and, for the last tap,
 * where it was and what the browser hit there — with a dot on the spot.
 * ponytail: a probe for one class of iOS bug; delete once taps are settled.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { TEST_SITE } from "@/features/funnel/components/entry";

const KEY = "secret-missions-debug";

const enabled = () => {
  if (!TEST_SITE) return false;
  try {
    const q = new URLSearchParams(window.location.search).get("debug");
    if (q === "1") localStorage.setItem(KEY, "1");
    if (q === "0") localStorage.removeItem(KEY);
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
};

const describe = (el: Element | null) => {
  if (!el) return "nothing";
  const h = el as HTMLElement;
  const what =
    h.getAttribute("aria-label") ||
    h.getAttribute("alt") ||
    (h.textContent || "").trim().slice(0, 24);
  return `${el.tagName.toLowerCase()}${what ? ` "${what}"` : ""}`;
};

const bounds = (el: Element | null) => {
  if (!el) return "none";
  const r = el.getBoundingClientRect();
  return `${Math.round(r.x)},${Math.round(r.y)} ${Math.round(r.width)}x${Math.round(r.height)}`;
};

export function TapDebug() {
  const [on] = useState(enabled);
  const [line, setLine] = useState("");
  const [tap, setTap] = useState<{
    x: number;
    y: number;
    hit: string;
    target: string;
  } | null>(null);
  const [click, setClick] = useState("");
  const [release, setRelease] = useState("");
  useEffect(() => {
    if (!on) return;
    const vv = window.visualViewport;
    const read = () =>
      setLine(
        `scroll ${Math.round(window.scrollX)},${Math.round(window.scrollY)} · ` +
          `inner ${window.innerWidth}x${window.innerHeight} · ` +
          `vv ${vv ? `${Math.round(vv.width)}x${Math.round(vv.height)} @${Math.round(vv.offsetLeft)},${Math.round(vv.offsetTop)} z${vv.scale.toFixed(2)}` : "-"}` +
          ` · pageY ${Math.round(vv?.pageTop ?? window.scrollY)}\n` +
          `frame ${bounds(document.querySelector("[data-sm-player]"))} · bodyY ${Math.round(document.body.getBoundingClientRect().y)}`,
      );
    const onDown = (e: PointerEvent) => {
      read();
      setTap({
        x: e.clientX,
        y: e.clientY,
        hit: describe(document.elementFromPoint(e.clientX, e.clientY)),
        target: `${describe(e.target instanceof Element ? e.target : null)} [${bounds(e.target instanceof Element ? e.target : null)}]`,
      });
    };
    const onClick = (e: MouseEvent) => {
      setClick(
        `${Math.round(e.clientX)},${Math.round(e.clientY)} → ${describe(e.target instanceof Element ? e.target : null)}`,
      );
    };
    const onUp = (e: PointerEvent) =>
      setRelease(
        `${e.type} ${Math.round(e.clientX)},${Math.round(e.clientY)} → ${describe(e.target instanceof Element ? e.target : null)}`,
      );
    read();
    const t = setInterval(read, 500);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("pointerup", onUp, true);
    document.addEventListener("pointercancel", onUp, true);
    return () => {
      clearInterval(t);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("pointerup", onUp, true);
      document.removeEventListener("pointercancel", onUp, true);
    };
  }, [on]);
  if (!on) return null;
  // Client coordinates belong to the viewport. The player uses layout
  // containment, so a fixed descendant there would be offset by its frame.
  return createPortal(
    <>
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[1000] bg-black/80 px-2 py-1 font-mono text-[10px] leading-tight text-[#7CFC00]">
        <div className="whitespace-pre-line">{line}</div>
        {tap ? (
          <div>
            tap {Math.round(tap.x)},{Math.round(tap.y)} → {tap.target}
            <br />
            at point: {tap.hit}
          </div>
        ) : null}
        {release ? <div>{release}</div> : null}
        {click ? <div>click {click}</div> : null}
      </div>
      {tap ? (
        <div
          data-tap-marker
          style={{ left: tap.x, top: tap.y }}
          className="pointer-events-none fixed z-[1000] size-4 -translate-1/2 rounded-full border-2 border-[#7CFC00]"
        />
      ) : null}
    </>,
    document.body,
  );
}
